import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, Select, Alert, App as AntApp } from 'antd';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Encrypt } from '../../utils/encryption';
import { countryCodes, mobileForApi, isMobileLengthValid, MOBILE_MAX_LEN } from '../../constants/CountryCodes';
import { userLogin } from '../../config/api';
import { AUTH } from '../../config/zinihubConfig';
import styles from './Login.module.css';

function Login() {
  const { message } = AntApp.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { setToken } = useAuth();

  const onFinish = async (values) => {
    const userName = mobileForApi(values.prefix, values.mobile);
    setLoading(true);
    try {
      const responsefromLambda = await axios.post(
        userLogin,
        Encrypt({ userName, password: values.password }),
        { headers: { 'Content-Type': 'application/json' } }
      );
      const data = responsefromLambda.data;

      if (data.token) {
        setToken(data.token);
        message.success(AUTH.LOGIN_SUCCESS);
        navigate('/', { replace: true });
      } else {
        setError(
          typeof data?.message === 'string' && data.message.trim()
            ? data.message
            : AUTH.LOGIN_ERROR_DEFAULT
        );
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.message || AUTH.LOGIN_ERROR_UNKNOWN);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          closable
          className={styles.alert}
          onClose={() => setError('')}
        />
      )}
      <h1 className={styles.title}>{AUTH.LOGIN_TITLE}</h1>

      <Form form={form} layout="vertical" onFinish={onFinish} autoComplete="off">
        <div className={styles.fieldGroup}>
          <div className={styles.fieldLabel}>
            <span className={styles.requiredMark}>*</span>
            {AUTH.LOGIN_PHONE_LABEL.replace(/^\*\s*/, '')}
          </div>
          <div className={styles.mobileRow}>
            <Form.Item name="prefix" initialValue="+91" className={styles.countryCode}>
              <Select
                showSearch
                style={{ width: 88 }}
                popupMatchSelectWidth={false}
                dropdownStyle={{ minWidth: 180 }}
                options={countryCodes.map((cc) => ({
                  value: cc.value,
                  label: cc.label,
                }))}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>

            <Form.Item
              name="mobile"
              className={styles.mobileInput}
              rules={[
                { required: true, message: AUTH.LOGIN_PHONE_REQUIRED },
                {
                  validator: (_, value) => {
                    if (!value || !isMobileLengthValid(value)) {
                      return Promise.reject(new Error(AUTH.LOGIN_PHONE_INVALID_LENGTH));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                placeholder={AUTH.LOGIN_PHONE_PLACEHOLDER}
                maxLength={MOBILE_MAX_LEN}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '').slice(0, MOBILE_MAX_LEN);
                  form.setFieldsValue({ mobile: v });
                }}
              />
            </Form.Item>
          </div>
        </div>

        <Form.Item
          label={AUTH.LOGIN_PASSWORD_LABEL_PLAIN}
          name="password"
          rules={[{ required: true, message: AUTH.LOGIN_PASSWORD_REQUIRED }]}
          className={styles.passwordField}
        >
          <Input.Password className={styles.passwordInput} />
        </Form.Item>

        <div className={styles.forgotLink} onClick={() => navigate('/resetpassword')}>{AUTH.LOGIN_LINK_RESET}</div>

        <Form.Item className={styles.submitBtn}>
          <Button type="primary" htmlType="submit" loading={loading} className={styles.submitBtn}>
            {loading ? AUTH.LOGIN_SUBMITTING : AUTH.LOGIN_SUBMIT}
          </Button>
        </Form.Item>

        <div className={styles.registerLink}>
          {AUTH.LOGIN_LINK_NEW_USER}
          <span className={styles.linkLike} onClick={() => navigate('/register')}>{AUTH.LOGIN_LINK_CREATE_ACCOUNT}</span>
        </div>
      </Form>
    </>
  );
}

export default Login;
